// ZIP-Archiv ohne Kompression („stored") — für „Ordner herunterladen" in der
// Alten Kundenakte (PO 28.09.2026).
//
// Warum selbst gebaut statt einer Bibliothek: Die Akte besteht fast nur aus
// PDFs, Bildern und Office-Dateien, die bereits komprimiert sind — ein Deflate
// spart dort kaum etwas und kostet Zeit. „Stored" ist dagegen ein paar Dutzend
// Zeilen Format (PKWARE APPNOTE 4.3/4.4) ohne neue Abhängigkeit.
//
// Grenzen: kein ZIP64, also höchstens 65.535 Einträge und 4 GB — der Aufrufer
// prüft das vorher (`ZIP_GRENZE_BYTES`).

export const ZIP_GRENZE_BYTES = 0xffffffff;
export const ZIP_GRENZE_EINTRAEGE = 0xffff;

export type ZipEintrag = { name: string; daten: Uint8Array; geaendert?: Date };

let crcTabelle: Uint32Array | null = null;
const tabelle = (): Uint32Array => {
  if (crcTabelle) return crcTabelle;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  crcTabelle = t;
  return t;
};

export const crc32 = (daten: Uint8Array): number => {
  const t = tabelle();
  let c = 0xffffffff;
  for (let i = 0; i < daten.length; i++) c = t[(c ^ daten[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

/** DOS-Zeit und -Datum, wie ZIP sie erwartet (lokale Zeit, 2-Sekunden-Raster). */
const dosZeit = (d: Date): { zeit: number; datum: number } => {
  const jahr = Math.max(1980, d.getFullYear());
  return {
    zeit: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    datum: ((jahr - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
};

/**
 * Doppelte Namen bekommen „ (2)", „ (3)" … vor der Endung — ein ZIP mit zwei
 * gleichnamigen Einträgen entpackt Windows nur mit Rückfrage.
 */
export const eindeutigeNamen = (namen: readonly string[]): string[] => {
  const belegt = new Set<string>();
  return namen.map((n) => {
    let kandidat = n;
    const punkt = n.lastIndexOf(".");
    const schraeg = n.lastIndexOf("/");
    const [stamm, endung] = punkt > schraeg + 1 ? [n.slice(0, punkt), n.slice(punkt)] : [n, ""];
    for (let i = 2; belegt.has(kandidat.toLowerCase()); i++) kandidat = `${stamm} (${i})${endung}`;
    belegt.add(kandidat.toLowerCase());
    return kandidat;
  });
};

export const baueZip = (eintraege: readonly ZipEintrag[]): Blob => {
  const enc = new TextEncoder();
  const teile: BlobPart[] = [];
  const zentral: Uint8Array[] = [];
  let offset = 0;

  for (const e of eintraege) {
    const name = enc.encode(e.name);
    const crc = crc32(e.daten);
    const { zeit, datum } = dosZeit(e.geaendert ?? new Date());
    // Bit 11: Dateiname ist UTF-8 — sonst zeigt Windows Umlaute falsch an.
    const flags = 0x0800;

    const lokal = new DataView(new ArrayBuffer(30));
    lokal.setUint32(0, 0x04034b50, true);
    lokal.setUint16(4, 20, true);
    lokal.setUint16(6, flags, true);
    lokal.setUint16(8, 0, true); // stored
    lokal.setUint16(10, zeit, true);
    lokal.setUint16(12, datum, true);
    lokal.setUint32(14, crc, true);
    lokal.setUint32(18, e.daten.length, true);
    lokal.setUint32(22, e.daten.length, true);
    lokal.setUint16(26, name.length, true);
    lokal.setUint16(28, 0, true);

    const z = new DataView(new ArrayBuffer(46 + name.length));
    z.setUint32(0, 0x02014b50, true);
    z.setUint16(4, 20, true);
    z.setUint16(6, 20, true);
    z.setUint16(8, flags, true);
    z.setUint16(10, 0, true);
    z.setUint16(12, zeit, true);
    z.setUint16(14, datum, true);
    z.setUint32(16, crc, true);
    z.setUint32(20, e.daten.length, true);
    z.setUint32(24, e.daten.length, true);
    z.setUint16(28, name.length, true);
    z.setUint32(42, offset, true);
    new Uint8Array(z.buffer).set(name, 46);
    zentral.push(new Uint8Array(z.buffer));

    // Die Casts nur wegen der TS-Typen (ArrayBufferLike vs. ArrayBuffer).
    teile.push(lokal.buffer, name as BlobPart, e.daten as BlobPart);
    offset += 30 + name.length + e.daten.length;
  }

  const zentralLaenge = zentral.reduce((n, z) => n + z.length, 0);
  const ende = new DataView(new ArrayBuffer(22));
  ende.setUint32(0, 0x06054b50, true);
  ende.setUint16(8, eintraege.length, true);
  ende.setUint16(10, eintraege.length, true);
  ende.setUint32(12, zentralLaenge, true);
  ende.setUint32(16, offset, true);

  return new Blob([...teile, ...(zentral as BlobPart[]), ende.buffer], { type: "application/zip" });
};
